import { useEffect, useState } from "react";
import "./AnalyticsModal.css";
import apiService from "../services/apiService";

const VIEW_OPTIONS = [
  { id: "agent", label: "By Agent", disabled: false },
  { id: "category", label: "By Category", disabled: false },
];

const RANGE_OPTIONS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 Days" },
  { id: "30d", label: "30 Days" },
  { id: "90d", label: "90 Days" },
];

const EMPTY_SUMMARY = {
  avgAnalysisMinutes: 0,
  emailsHandled: 0,
  detailedCompletionPercent: 0,
};

const formatMetric = (value, digits = 0) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0";
  if (digits === 0) return `${Math.round(numericValue)}`;
  return `${numericValue.toFixed(digits).replace(/\.0$/, "")}`;
};

const getRowTitle = (view, row) =>
  view === "agent" ? row.displayName || row.userEmail || "Unknown Agent" : row.category || "Uncategorized";

const getRowSubtitle = (emailsHandled) =>
  `${emailsHandled} ${emailsHandled === 1 ? "email" : "emails"}`;

const AnalyticsModal = ({ onClose, userEmail }) => {
  const [selectedView, setSelectedView] = useState("agent");
  const [selectedRange, setSelectedRange] = useState("7d");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let isCancelled = false;

    const loadAnalytics = async () => {
      if (!userEmail) {
        setAnalyticsData(null);
        setError("Unable to read your Outlook email address. Please reopen the email and try again.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await apiService.getAnalytics(userEmail, selectedRange, selectedView);
        if (!isCancelled) {
          setAnalyticsData(response);
        }
      } catch (loadError) {
        if (!isCancelled) {
          console.error("Analytics load failed:", loadError);
          setAnalyticsData(null);
          setError("Unable to load analytics right now.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadAnalytics();
    return () => {
      isCancelled = true;
    };
  }, [refreshKey, selectedRange, selectedView, userEmail]);

  const isAdmin = analyticsData?.isAdmin || false;
  const rows = Array.isArray(analyticsData?.rows) ? analyticsData.rows : [];
  const summary = analyticsData?.summary || EMPTY_SUMMARY;
  const modalTitle = isAdmin ? "Manager Analytics" : "My Analytics";
  const avgLabel = isAdmin ? "Team Avg (min)" : "My Avg (min)";
  const sectionTitle = selectedView === "agent" ? "Agent Performance" : "Category Breakdown";

  return (
    <div className="analytics-screen slide-up">
      <div className="analytics-screen-inner">
        <div className="analytics-header">
          <div className="analytics-title-block">
            <div className="analytics-title-row">
              <svg className="analytics-title-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="20" x2="20" y2="20" />
                <rect x="5" y="10" width="3" height="8" rx="1" />
                <rect x="11" y="6" width="3" height="12" rx="1" />
                <rect x="17" y="3" width="3" height="15" rx="1" />
              </svg>
              <h2>{modalTitle}</h2>
            </div>
          </div>

          <button className="analytics-close-btn" onClick={onClose} aria-label="Close analytics">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="analytics-controls">
          <div className="analytics-tabs" role="tablist" aria-label="Analytics views">
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-tab ${selectedView === option.id ? "analytics-tab--active" : ""}`}
                onClick={() => setSelectedView(option.id)}
              >
                <span>{option.label}</span>
              </button>
            ))}
          </div>

          <div className="analytics-ranges" role="tablist" aria-label="Analytics ranges">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-range-btn ${selectedRange === option.id ? "analytics-range-btn--active" : ""}`}
                onClick={() => setSelectedRange(option.id)}
              >
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="analytics-body">
          {loading ? (
            <div className="analytics-state-card">
              <span className="spinner" /> Loading analytics...
            </div>
          ) : error ? (
            <div className="analytics-state-card analytics-state-card--error">
              <p>{error}</p>
              <button type="button" className="analytics-retry-btn" onClick={() => setRefreshKey((value) => value + 1)}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="analytics-summary-grid">
                <div className="analytics-summary-card">
                  <div className="analytics-summary-value analytics-summary-value--blue">
                    {formatMetric(summary.avgAnalysisMinutes, 1)}
                  </div>
                  <div className="analytics-summary-label">{avgLabel}</div>
                </div>

                <div className="analytics-summary-card">
                  <div className="analytics-summary-value">
                    {formatMetric(summary.emailsHandled)}
                  </div>
                  <div className="analytics-summary-label">Emails Handled</div>
                </div>

                <div className="analytics-summary-card">
                  <div className="analytics-summary-value analytics-summary-value--green">
                    {formatMetric(summary.detailedCompletionPercent)}%
                  </div>
                  <div className="analytics-summary-label">Detailed Completion</div>
                </div>
              </div>

              <div className="analytics-section-header">
                <h3>{sectionTitle}</h3>
              </div>

              {rows.length ? (
                <div className="analytics-list">
                  {rows.map((row) => (
                    <div key={row.key} className="analytics-row-card">
                      <div className="analytics-row-main">
                        <div className="analytics-row-title">{getRowTitle(selectedView, row)}</div>
                        <div className="analytics-row-subtitle">{getRowSubtitle(row.emailsHandled || 0)}</div>
                      </div>

                      <div className="analytics-row-metric analytics-row-metric--completion">
                        {formatMetric(row.detailedCompletionPercent)}% detailed
                      </div>

                      <div className="analytics-row-metric analytics-row-metric--time">
                        {formatMetric(row.avgAnalysisMinutes, 1)} min
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="analytics-state-card">
                  No Outlook analytics found for this range yet.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModal;
